import { useQueryClient } from '@tanstack/react-query'
import { TariffFormModelDto } from './dto/tariffs-form.dto'
import { TariffDto } from './dto/tariffs.dto'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import axios from 'axios'
import { addTariffApiErrors } from './dto/tariffs-api-errors'
import { ApiResult, usePortalMutation } from 'src/Libs/api'

interface UseAddTariffHook {
  isLoading: boolean
  addTariff: (payload: TariffFormModelDto) => void
}

/**
 * Function for add new tariff data
 * @param payload - data off TariffFormModelDto added to mutate react-query function
 * @param onClose - function for closing dialog on success method
 * @param installationId - unique installation identifier as a string
 * @returns isLoading as boolean and isError as boolean
 * @returns mutate function from react-query
 */
export const useAddTariff = (onClose: () => void, installationId: number): UseAddTariffHook => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResult()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const queryClient = useQueryClient()

  const addTariffApi = async (payload: TariffFormModelDto): Promise<ApiResult<string | TariffDto>> => {
    const tariffResponse = await authFetch<TariffDto>({
      path: 'tariffs',
      endpoint: process.env.REACT_APP_HAGER_BRIDGE_SERVICE,
      method: 'POST',
      payload: payload,
    })

    return new ApiResult(tariffResponse)
  }

  const { isPending, mutate: addTariff } = usePortalMutation({
    mutationFn: addTariffApi,
    onError: error => {
      if (axios.isAxiosError(error)) {
        enqueueErrorStack(t(addTariffApiErrors[error.response?.data.errorCode]))
      } else {
        enqueueErrorStack(t('emobility.tariffs.add-tariff-failed'))
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tariffs', installationId] })
      enqueueSuccessStack(t('emobility.tariffs.add-tariff-success'))
      onClose()
    },
  })

  return { isLoading: isPending, addTariff }
}
